import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollSmoother } from 'gsap/ScrollSmoother';
import { autoPlayVideo, autoStopVideo } from './video';

if (window.innerWidth < 1024) {
  throw new Error('Animations not supported');
}

// GSAP Scroll Trigger Animations
gsap.registerPlugin(ScrollTrigger, ScrollSmoother);

// Smooth Scroller
const smoother = ScrollSmoother.create({
  smooth: 1.25,            // how long (in seconds) it takes to "catch up" to the native scroll position
  effects: true,           // looks for data-speed and data-lag attributes on elements
  smoothTouch: 0.1,        // much shorter smoothing time on touch devices (default is NO smoothing on touch devices)
});

smoother.effects('.parallax-img', { speed: 'auto' });

function responsiveValues(mobile, desktop) {
  return (window.innerWidth < 769) ? mobile : desktop;
}

function changeParallaxBg(sectionNum = 1) {
  if (window.innerWidth > 1023) {
    document.getElementById('lp-bg').style.backgroundImage = `url(./images/lp-texture-${sectionNum}.webp)`;
  }
}

const beforeLineWidth = sectionId => {
  const section = document.getElementById(sectionId);
  const necessaryLength = Math.round( (window.innerHeight - section.getBoundingClientRect().height) );
  const mobileNecessaryLength = Math.round( window.innerHeight / 2 );
  return responsiveValues(mobileNecessaryLength, necessaryLength);
};

const masterTimeline = gsap.timeline();

let timeOut;

const handleAnimation = ({ isActive, trigger }) => {
  if (isActive) {
    timeOut = setTimeout(() => {
      autoPlayVideo(trigger.id);
    }, 1000);
  } else {
    clearTimeout(timeOut);
    autoStopVideo(trigger.id);
  }
}

const beforeLineInitialTop = (nextSection) => `-${document.getElementById(nextSection).offsetHeight}px`;

const initialBeforeLineWidth = 0.1;

///////////////////////////////////////////////////////////////////////////

/* Section 1 - Paragraph */

const firstLine = document.querySelector('#section_1 .before-line');

// Reset
gsap.set('#section_1 > svg', { autoAlpha: 0 });
gsap.set('#section_1 > p', { autoAlpha: 0 });
gsap.set('#section_1 .before-line', { top: '50%', width: '6%' });

function section1() {

  const tl1 = gsap.timeline();

  // Paragraph
  tl1
  .fromTo('#section_1 > p', {
    autoAlpha: 0,
  }, {
    autoAlpha: 1,
    delay: 0.4,
  })
  // SVG
  .fromTo('#section_1 > svg', {
    autoAlpha: 0,
  }, {
    autoAlpha: 1,
    delay: -0.4,
  })
  .to('#section_1 .before-line', {
    scrollTrigger: {
      start: '0 20%',
      end: '+=200%',
      scrub: 0,
    },
    width: window.innerHeight / 2,
  })
  .to('#section_1', {
    scrollTrigger: {
      trigger: '#section_1',
      pin: '#section_1',
      start: '0% 10%',
      end: '+=800',
      scrub: 1,
    }
  });

  return tl1;
}

///////////////////////////////////////////////////////////////////////////

/* Section 2 - Meet the Mad Scientists */

// Resets
gsap.set('#section_2 > .section-number', { autoAlpha: 0, y: 500 });
gsap.set('#section_2 > .video-container', { autoAlpha: 0, y: 600 });
gsap.set('#section_2 .video-color-cover', { zIndex: responsiveValues(1, 2) });
gsap.set('#section_2 .hover-text', { y: -200, autoAlpha: 0 });
gsap.set('#section_2 .cta-play', { autoAlpha: 0, scale: .1 });

function section2() {

  const screenLineFactor = window.innerWidth < 1900 ? '-50%' : `-${window.innerHeight / 2.5}px`;

  const tl2 = gsap.timeline();

  tl2
    .to('#section_2', {
      scrollTrigger: {
        trigger: '#section_2',
        pin: true,
        anticipatePin: 1,
        start: '58% 50%',
        end: '+=75%',
        scrub: 1,
        onEnter: handleAnimation,
        onEnterBack: handleAnimation,
        onLeave: handleAnimation,
        onLeaveBack: handleAnimation,
        onToggle: self => {
          if (self.isActive) {
            document.getElementById('section_2').prepend(firstLine);
            firstLine.style.top = screenLineFactor;
          }
          else {
            if (self.direction < 0) {
              document.getElementById('section_1').prepend(firstLine);
              firstLine.style.top = '50%';
            }
          }
        }
      },
    })
    // Big Number
    .to('#section_2 > .section-number', {
      scrollTrigger: {
          trigger: '#section_2',
          start: '0% 50%',
          end: '58% 50%',
          scrub: 1,
      },
      autoAlpha: 1,
      y: 0,
      ease: 'back',
    })
    // Video
    .to('#section_2 > .video-container', {
      scrollTrigger: {
        trigger: '#section_2',
        start: '50% 50%',
        end: '80% 50%',
        scrub: 1,
      },
      autoAlpha: 1,
      y: 0,
      ease: 'power1.out',
    })
    // Hover Text
    .to('#section_2 .hover-text', {
      scrollTrigger: {
        trigger: '#section_2',
        start: '58% 50%',
        end: '110% 50%',
        scrub: 1,
      },
      y: 0,
      autoAlpha: 1,
      ease: 'back',
    })
    // CTA Play
    .to('#section_2 .cta-play', {
      scrollTrigger: {
        trigger: '#section_2',
        scrub: 1,
        start: '58% 50%',
        end: '110% 50%',
      },
      autoAlpha: 1,
      scale: 1,
      ease: 'back'
    });

  return tl2;
}

///////////////////////////////////////////////////////////////////////////

/* Section 3 - Our Secret Sauce */

// Resets
gsap.set('#section_3 .before-line', { top: beforeLineInitialTop('section_3'), width: initialBeforeLineWidth });
gsap.set('#section_3 > .section-number', { autoAlpha: 0, y: 500 });
gsap.set('#section_3 > .video-container', { autoAlpha: 0, y: 600 });
gsap.set('#section_3 .video-color-cover', { zIndex: 2 });
gsap.set('#section_3 .hover-text', { y: -200, autoAlpha: 0 });
gsap.set('#section_3 .cta-play', { autoAlpha: 0, scale: .1 });

function section3() {

  const tl3 = gsap.timeline();

  tl3
    .to('#section_3', {
      scrollTrigger: {
        trigger: '#section_3',
        pin: true,
        anticipatePin: 1,
        start: '58% 50%',
        end: '+=75%',
        scrub: 1,
        onEnter: handleAnimation,
        onEnterBack: handleAnimation,
        onLeave: handleAnimation,
        onLeaveBack: handleAnimation,
      },
    })
    // Before Line
    .to('#section_3 .before-line', {
      scrollTrigger: {
        trigger: '#section_2',
        start: '0 10%',
        end: '200%',
        scrub: 0,
      },
      top: `-${beforeLineWidth('section_3')}`,
      width: beforeLineWidth('section_3'),
    })
    // Big Number
    .to('#section_3 > .section-number', {
      scrollTrigger: {
          trigger: '#section_3',
          start: '-10% 50%',
          end: '58% 50%',
          scrub: 1,
      },
      autoAlpha: 1,
      y: 0,
      ease: 'back',
    })
    // Video
    .to('#section_3 > .video-container', {
      scrollTrigger: {
        trigger: '#section_3',
        start: '35% 50%',
        end: '60% 50%',
        scrub: 1,
      },
      autoAlpha: 1,
      y: 0,
      ease: 'power1.out',
    })
    // Hover Text
    .to('#section_3 .hover-text', {
      scrollTrigger: {
        trigger: '#section_3',
        start: '52% 50%',
        end: '105% 50%',
        scrub: 1,
      },
      y: 0,
      autoAlpha: 1,
      ease: 'back',
    })
    // CTA Play
    .to('#section_3 .cta-play', {
      scrollTrigger: {
        trigger: '#section_3',
        scrub: 1,
        start: '52% 50%',
        end: '105% 50%',
      },
      autoAlpha: 1,
      scale: 1,
      ease: 'back'
    });

    return tl3;
}

///////////////////////////////////////////////////////////////////////////

/* Section 4 - What Drive Us */

// Resets
gsap.set('#section_4 .before-line', { top: beforeLineInitialTop('section_4'), width: initialBeforeLineWidth });
gsap.set('#section_4 > .section-number', { autoAlpha: 0, y: 500 });
gsap.set('#section_4 > .video-container', { autoAlpha: 0, y: 600 });
gsap.set('#section_4 .video-color-cover', { zIndex: 2 });
gsap.set('#section_4 .hover-text', { y: -200, autoAlpha: 0 });
gsap.set('#section_4 .cta-play', { autoAlpha: 0, scale: .1 });

function section4() {

  const tl4 = gsap.timeline();

  tl4
    .to('#section_4', {
      scrollTrigger: {
        trigger: '#section_4',
        pin: true,
        anticipatePin: 1,
        start: '58% 50%',
        end: '+=75%',
        scrub: 1,
        onEnter: handleAnimation,
        onEnterBack: handleAnimation,
        onLeave: handleAnimation,
        onLeaveBack: handleAnimation,
      },
    })
    // Before Line
    .to('#section_4 .before-line', {
      scrollTrigger: {
        trigger: '#section_3',
        start: '0 10%',
        end: '200%',
        scrub: 0,
      },
      top: `-${beforeLineWidth('section_4')}`,
      width: beforeLineWidth('section_4'),
    })
    // Big Number
    .to('#section_4 > .section-number', {
      scrollTrigger: {
          trigger: '#section_4',
          start: '-10% 50%',
          end: '58% 50%',
          scrub: 1,
      },
      autoAlpha: 1,
      y: 0,
      ease: 'back',
    })
    // Video
    .to('#section_4 > .video-container', {
      scrollTrigger: {
        trigger: '#section_4',
        start: '35% 50%',
        end: '60% 50%',
        scrub: 1,
      },
      autoAlpha: 1,
      y: 0,
      ease: 'power1.out',
    })
    // Hover Text
    .to('#section_4 .hover-text', {
      scrollTrigger: {
        trigger: '#section_4',
        start: '52% 50%',
        end: '105% 50%',
        scrub: 1,
      },
      y: 0,
      autoAlpha: 1,
      ease: 'back',
    })
    // CTA Play
    .to('#section_4 .cta-play', {
      scrollTrigger: {
        trigger: '#section_4',
        scrub: 1,
        start: '52% 50%',
        end: '105% 50%',
      },
      autoAlpha: 1,
      scale: 1,
      ease: 'back'
    });

  return tl4;

}

///////////////////////////////////////////////////////////////////////////

/* Section 5 - Expert Craftmanship */

// Resets
gsap.set('#section_5 .before-line', { top: beforeLineInitialTop('section_5'), width: initialBeforeLineWidth });
gsap.set('#section_5', { autoAlpha: 0 });

function section5() {

  const tl5 = gsap.timeline();

  tl5
  .to('#section_5', {
      scrollTrigger: {
        trigger: '#section_5',
        start: '0% 70%',
        end: '50% 0%',
        scrub: 1,
        onUpdate: self => {
          const fixed = self.progress.toFixed(1);
          if (fixed === '0.4') {
            changeParallaxBg(1);
          }
          if (fixed === '0.6') {
            changeParallaxBg(1);
          }
        }
      },
      autoAlpha: 1,
      ease: 'power2.out',
    })
    // Before Line
    .to('#section_5 .before-line', {
      scrollTrigger: {
        trigger: '#section_4',
        start: '0 10%',
        end: '200%',
        scrub: 0,
      },
      top: `-${beforeLineWidth('section_5')}`,
      width: beforeLineWidth('section_5'),
    })
    // "Expert" Text
    .from('#section_5 span:first-child', {
      scrollTrigger: {
        trigger: '#section_5',
        start: '15% 50%',
        end: '50% 50%',
        scrub: 1,
      },
      x: '100%',
      ease: 'power2.inOut',
    })
    // "Craftmanship" Text
    .from('#section_5 span:last-child', {
      scrollTrigger: {
        trigger: '#section_5',
        start: '15% 50%',
        end: '50% 50%',
        scrub: 1,
      },
      x: '-100%',
      ease: 'power2.inOut',
    });

  return tl5;
}

///////////////////////////////////////////////////////////////////////////

/* Section 6 - Maximum Engineering */

// Resets
gsap.set('#section_6 .before-line', { top: beforeLineInitialTop('section_6'), width: initialBeforeLineWidth });
gsap.set('#section_6 > .section-number', { autoAlpha: 0, y: 500 });
gsap.set('#section_6 > .video-container', { autoAlpha: 0, y: 600 });
gsap.set('#section_6 .video-color-cover', { zIndex: 2 });
gsap.set('#section_6 .hover-text', { y: -200, autoAlpha: 0 });
gsap.set('#section_6 .cta-play', { autoAlpha: 0, scale: .1 });

function section6() {

  const tl6 = gsap.timeline();

  tl6
    .to('#section_6', {
      scrollTrigger: {
        trigger: '#section_6',
        pin: true,
        anticipatePin: 1,
        start: '58% 50%',
        end: '+=75%',
        scrub: 1,
        onEnter: handleAnimation,
        onEnterBack: handleAnimation,
        onLeave: handleAnimation,
        onLeaveBack: handleAnimation,
      },
    })
    // Before Line
    .to('#section_6 .before-line', {
      scrollTrigger: {
        trigger: '#section_5',
        start: '0 10%',
        end: '200%',
        scrub: 0,
      },
      top: `-${beforeLineWidth('section_5')}`,
      width: beforeLineWidth('section_5'),
    })
    // Big Number
    .to('#section_6 > .section-number', {
      scrollTrigger: {
          trigger: '#section_6',
          start: '-10% 50%',
          end: '58% 50%',
          scrub: 1,
      },
      autoAlpha: 1,
      y: 0,
      ease: 'back',
    })
    // Video
    .to('#section_6 > .video-container', {
      scrollTrigger: {
        trigger: '#section_6',
        start: '35% 50%',
        end: '60% 50%',
        scrub: 1,
      },
      autoAlpha: 1,
      y: 0,
      ease: 'power1.out',
    })
    // Hover Text
    .to('#section_6 .hover-text', {
      scrollTrigger: {
        trigger: '#section_6',
        start: '52% 50%',
        end: '105% 50%',
        scrub: 1,
      },
      y: 0,
      autoAlpha: 1,
      ease: 'back',
    })
    // CTA Play
    .to('#section_6 .cta-play', {
      scrollTrigger: {
        trigger: '#section_6',
        scrub: 1,
        start: '52% 50%',
        end: '105% 50%',
      },
      autoAlpha: 1,
      scale: 1,
      ease: 'back'
    });

  return tl6;
}

///////////////////////////////////////////////////////////////////////////

/* Section 7 - Unmatched Performance */

// Resets
gsap.set('#section_7 .before-line', { top: beforeLineInitialTop('section_7'), width: initialBeforeLineWidth });
gsap.set('#section_7 > .section-number', { autoAlpha: 0, y: 500 });
gsap.set('#section_7 > .video-container', { autoAlpha: 0, y: 700 });
gsap.set('#section_7 .video-color-cover', { zIndex: 2 });
gsap.set('#section_7 .hover-text', { y: -200, autoAlpha: 0 });
gsap.set('#section_7 .cta-play', { autoAlpha: 0, scale: .1 });

function section7() {

  const tl7 = gsap.timeline();

  tl7
    .to('#section_7', {
      scrollTrigger: {
        trigger: '#section_7',
        pin: true,
        anticipatePin: 1,
        start: '58% 50%',
        end: '+=75%',
        scrub: 1,
        onEnter: handleAnimation,
        onEnterBack: handleAnimation,
        onLeave: handleAnimation,
        onLeaveBack: handleAnimation,
      },
    })
    // Before Line
    .to('#section_7 .before-line', {
      scrollTrigger: {
        trigger: '#section_6',
        start: '0 10%',
        end: '200%',
        scrub: 0,
      },
      top: `-${beforeLineWidth('section_7')}`,
      width: beforeLineWidth('section_7'),
    })
    // Big Number
    .to('#section_7 > .section-number', {
      scrollTrigger: {
          trigger: '#section_7',
          start: '-10% 50%',
          end: '58% 50%',
          scrub: 1,
      },
      autoAlpha: 1,
      y: 0,
      ease: 'back',
    })
    // Video
    .to('#section_7 > .video-container', {
      scrollTrigger: {
        trigger: '#section_7',
        start: '35% 50%',
        end: '60% 50%',
        scrub: 1,
      },
      autoAlpha: 1,
      y: 0,
      ease: 'power1.out',
    })
    // Hover Text
    .to('#section_7 .hover-text', {
      scrollTrigger: {
        trigger: '#section_7',
        start: '52% 50%',
        end: '105% 50%',
        scrub: 1,
      },
      y: 0,
      autoAlpha: 1,
      ease: 'back',
    })
    // CTA Play
    .to('#section_7 .cta-play', {
      scrollTrigger: {
        trigger: '#section_7',
        scrub: 1,
        start: '52% 50%',
        end: '105% 50%',
      },
      autoAlpha: 1,
      scale: 1,
      ease: 'back'
    });

  return tl7;
}

///////////////////////////////////////////////////////////////////////////

/* Section 8 - Get Inspired */

function section8() {

  const tl8 = gsap.timeline();

  const socialIcons = gsap.utils.toArray('.social-media a');

  // Social Block
  socialIcons.forEach((social, index) => {
    const factor = index + 0.5;
    gsap.to(social, {
      scrollTrigger: {
          trigger: '#section_8',
          start: '-10% 50%',
          end: '30% 50%',
          scrub: factor,
      },
      keyframes: {
        '0%': { scale: .5, autoAlpha: 0, yPercent: 250 },
        '100%': { scale: 1, autoAlpha: 1, yPercent: 0 },
        easeEach: 'power3.out',
      },
    });
  });

  tl8
    // Whole Section Bg
    .from('#section_8', {
      scrollTrigger: {
          trigger: '#section_8',
          start: '-15% 50%',
          end: '50% 50%',
          scrub: 1,
      },
    })
    // Heading
    .from('#section_8 h3', {
      scrollTrigger: {
          trigger: '#section_8',
          start: '-10% 50%',
          end: '50% 50%',
          scrub: 1,
      },
      ease: 'back',
      autoAlpha: 0,
      y: 80,
    })
    // Paragraph
    .from('#section_8 h3 + p', {
      scrollTrigger: {
          trigger: '#section_8',
          start: '10% 50%',
          end: '60% 50%',
          scrub: 1,
      },
      ease: 'back',
      autoAlpha: 0,
      y: 50,
    })
    // Slider
    .from('#section_8 .content > .glide', {
      scrollTrigger: {
          trigger: '#section_8',
          start: 'top 50%',
          end: '40% 50%',
          scrub: 1,
      },
      autoAlpha: 0,
      y: 200,
      scale: 0,
      rotateZ: -45
    });

  return tl8;
}

// Timeline Management

masterTimeline.add( section1() );
masterTimeline.add( section2() );
masterTimeline.add( section3() );
masterTimeline.add( section4() );
masterTimeline.add( section5() );
masterTimeline.add( section6() );
masterTimeline.add( section7() );
masterTimeline.add( section8() );
